import './RestaurantDetailPage.scss';
import locationIcon from './img/location.png';
import phoneIcon from './img/phone.png';
import searchIcon from './img/search-icon.png';
import addToCartIcon from './img/add-to-cart-icon.png';
import { Heading, SectionHeading } from '../Typography';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDeliveryAddress } from '../../address/DeliveryAddressContext';
import { fetchRestaurantDetails, restaurantDetailPath } from '../../services/api/restaurantService';
import { normalizeCoords } from '../../utils/geo';
import { formatDistanceMilesFromKm, isApiTruthy } from '../../utils/apiValues';
import { operatingStatusClass, resolveOrderBlock } from '../../utils/orderingMessages';
import generalLogo from '../AllRestaurants/img/general_logo.png';
import cuisineArrowIcon from '../TopCusines/img/move_right.png';
import StickyMenu from './StickyMenu';
import MenuItemCustomizeModal from './MenuItemCustomizeModal';
import OrderingBlockedModal from './OrderingBlockedModal';
import { IconClock, IconGlobe, IconStorefront } from './RestaurantDetailIcons';

const POPULAR_TAB_ID = '__popular__';
const POPULAR_SCROLL_ANIM_MS = 320;
const POPULAR_CARD_STRIDE_FALLBACK_PX = 260;
const SCROLL_EDGE_EPS = 4;
const TAB_CLICK_SCROLL_SUPPRESS_MS = 700;

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function applyImgFallback(e, fallbackSrc) {
  const img = e.currentTarget;
  if (!img || !fallbackSrc) return;
  if (img.dataset.fallbackApplied === '1') return;
  img.dataset.fallbackApplied = '1';
  img.src = fallbackSrc;
}

function readLocalizedName(value) {
  if (value && typeof value === 'object') {
    const en = value.en;
    if (en && String(en).trim()) return String(en).trim();
    const any = Object.values(value).find((v) => String(v || '').trim());
    return String(any || '').trim();
  }
  return String(value || '').trim();
}

function categoryKeyFromItem(item) {
  const fromRelation = item?.category?.uuid;
  const fromIds = Array.isArray(item?.category_ids) ? item.category_ids[0] : '';
  const fromRawId = item?.item_category_id || item?.category?.id;
  return String(fromRelation || fromIds || fromRawId || '__uncategorized__').trim();
}

function categoryLabelFromItem(item) {
  return readLocalizedName(item?.category?.category_name) || 'Other Items';
}

function categoryOrderFromItem(item, fallbackOrder) {
  const order = Number(item?.category?.order);
  return Number.isFinite(order) ? order : fallbackOrder;
}

function itemLabel(item) {
  const fromTranslation = Array.isArray(item?.translations)
    ? item.translations.find((t) => (t?.locale || '').toLowerCase() === 'en')?.item_name
    : '';
  return String(fromTranslation || item?.item_name || '').trim();
}

function itemDescription(item) {
  const fromTranslation = Array.isArray(item?.translations)
    ? item.translations.find((t) => (t?.locale || '').toLowerCase() === 'en')?.description
    : '';
  return String(fromTranslation || item?.description || '').replace(/^"+|"+$/g, '').trim();
}

/**
 * Returns true ONLY when the backend item has its own `image`
 * column populated ? meaning the merchant uploaded a real photo
 * for this item. In every other case we render the colored
 * fallback so the restaurant's brand logo / `food.svg` / banner
 * placeholders never appear on individual menu items.
 *
 * (The backend's `item_photo_url` accessor synthesises a stand-in
 * URL when `image` is empty, so we MUST gate on `image` itself.)
 */
function hasRealItemPhoto(item) {
  return String(item?.image ?? '').trim() !== '';
}

/**
 * Returns the merchant-defined item color exactly as the backend
 * supplied it (including #000000, which is the API's default and
 * also a valid choice). When the field is genuinely empty/missing
 * we return '' so the SCSS neutral background kicks in.
 */
function resolveItemColor(item) {
  const raw = String(item?.color ?? '').trim();
  if (!raw) return '';
  return raw;
}

/**
 * Picks black or white text to contrast against a hex background.
 * Uses relative luminance per WCAG; safe on any 3/6-digit hex.
 */
function readableTextOn(hexColor) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hexColor || '').trim());
  if (!m) return '#ffffff';
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.55 ? '#1a1a1a' : '#ffffff';
}

function itemInitial(item) {
  const label = itemLabel(item) || '?';
  const ch = label.trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

export default function RestaurantDetailPage(){
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const { currentAddress, orderMode, isPickup } = useDeliveryAddress();
  const userCoords = useMemo(() => normalizeCoords(currentAddress?.coords), [currentAddress]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restaurant, setRestaurant] = useState(null);
  const [items, setItems] = useState([]);
  const [activeMenuUuid, setActiveMenuUuid] = useState(POPULAR_TAB_ID);
  const [query, setQuery] = useState('');
  const tabsRef = useRef(null);
  const cardsRef = useRef(null);
  const stickyMenuSentinelRef = useRef(null);
  const isAutoScrollingRef = useRef(false);
  const suppressSpyTimeoutRef = useRef(0);
  const [isMenuStuck, setIsMenuStuck] = useState(false);
  const popularRef = useRef(null);
  const menuSectionRefs = useRef({});
  const popularScrollRef = useRef(null);
  const popularRafRef = useRef(0);
  const [popularScrollMeta, setPopularScrollMeta] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false,
  });
  const [customizeItem, setCustomizeItem] = useState(null);
  const [orderBlockModal, setOrderBlockModal] = useState(null);
  // Tracks per-item image-load failures so we can swap the broken
  // <img> for the colored fallback instead of showing the generic
  // `general_banner.png` placeholder.
  const [brokenImageIds, setBrokenImageIds] = useState(() => new Set());
  // Banner has two candidates from the API (`banner_url` and
  // `pick_of_the_day_banner_url`); we step through them on error
  // before falling back to the styled gradient placeholder.
  const [bannerCandidateIdx, setBannerCandidateIdx] = useState(0);

  const markImageBroken = (it) => {
    const key = String(it?.uuid || it?.id || '');
    if (!key) return;
    setBrokenImageIds((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const hasUsableImage = (it) =>
    hasRealItemPhoto(it) && !brokenImageIds.has(String(it?.uuid || it?.id || ''));

  const comboExtraItemsForModal = useMemo(() => {
    if (!customizeItem) return [];
    const sid = String(customizeItem.uuid || customizeItem.id);
    return items.filter((i) => String(i.uuid || i.id) !== sid).slice(0, 12);
  }, [items, customizeItem]);

  // Land on restaurant info (banner + name), not mid-page at the menu (listing scroll carries over).
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slugParam]);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const detailOpts = { signal: ac.signal, orderType: orderMode };
        if (userCoords) {
          detailOpts.latitude = userCoords.latitude;
          detailOpts.longitude = userCoords.longitude;
        }
        const res = await fetchRestaurantDetails(slugParam, detailOpts);
        if (cancelled) return;
        setRestaurant(res.restaurant);
        setItems(res.items);
        setActiveMenuUuid(POPULAR_TAB_ID);
        setBannerCandidateIdx(0);
        setBrokenImageIds(new Set());

        const canonicalPath = restaurantDetailPath(res.restaurant);
        const currentPath = `/restaurant/${encodeURIComponent(String(slugParam || '').trim())}`;
        if (canonicalPath.startsWith('/restaurant/') && canonicalPath !== currentPath) {
          navigate(canonicalPath, { replace: true });
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (!cancelled) setError(e.message || 'Could not load restaurant details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [slugParam, navigate, userCoords, orderMode, isPickup]);

  const orderBlock = useMemo(
    () => resolveOrderBlock({
      restaurant,
      hasAddress: !isPickup && !!userCoords,
      orderMode,
    }),
    [restaurant, userCoords, orderMode, isPickup]
  );
  const canOrder = !orderBlock;

  const handleAddItemClick = (it) => {
    if (orderBlock) {
      setOrderBlockModal(orderBlock);
      return;
    }
    setCustomizeItem(it);
  };

  const statusClass = operatingStatusClass(restaurant?.operating_status);
  const statusLabel = restaurant?.operating_status_label || (!isApiTruthy(restaurant?.is_open) ? 'Closed' : 'Open');
  const hoursLabel = restaurant?.hours_label || '\u2014';
  const branchDistanceLabel = formatDistanceMilesFromKm(restaurant?.distance_km);
  const websiteUrl = (restaurant?.website_url || '').trim();
  const phoneDisplay = (restaurant?.phone_number || '').trim() || '\u2014';

  useEffect(() => {
    return () => {
      if (popularRafRef.current) {
        cancelAnimationFrame(popularRafRef.current);
        popularRafRef.current = 0;
      }
      if (suppressSpyTimeoutRef.current) {
        window.clearTimeout(suppressSpyTimeoutRef.current);
        suppressSpyTimeoutRef.current = 0;
      }
    };
  }, []);

  const filteredItemsAll = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const name = itemLabel(it).toLowerCase();
      const desc = itemDescription(it).toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [items, query]);

  const categorySections = useMemo(() => {
    const byCategory = new Map();

    filteredItemsAll.forEach((item, index) => {
      const id = categoryKeyFromItem(item);
      if (!byCategory.has(id)) {
        byCategory.set(id, {
          id,
          label: categoryLabelFromItem(item),
          order: categoryOrderFromItem(item, index),
          firstIndex: index,
          items: [],
        });
      }
      byCategory.get(id).items.push(item);
    });

    return Array.from(byCategory.values())
      .sort((a, b) => (a.order - b.order) || (a.firstIndex - b.firstIndex));
  }, [filteredItemsAll]);

  const popularItems = useMemo(() => {
    // If backend provides an explicit "popular" signal later, swap this heuristic.
    const list = filteredItemsAll.slice();
    // Prefer items that have photos so the "Popular Items" row looks like the design.
    list.sort((a, b) => Number(hasRealItemPhoto(b)) - Number(hasRealItemPhoto(a)));
    return list.slice(0, 8);
  }, [filteredItemsAll]);

  const updatePopularScrollMeta = () => {
    const el = popularScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScrollLeft = scrollWidth - clientWidth;
    const hasOverflow = maxScrollLeft > SCROLL_EDGE_EPS;
    const canScrollLeft = hasOverflow && scrollLeft > SCROLL_EDGE_EPS;
    const canScrollRight = hasOverflow && scrollLeft < maxScrollLeft - SCROLL_EDGE_EPS;
    setPopularScrollMeta({ hasOverflow, canScrollLeft, canScrollRight });
  };

  useEffect(() => {
    updatePopularScrollMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popularItems.length]);

  useEffect(() => {
    const el = popularScrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => updatePopularScrollMeta());
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTabsNext = () => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: Math.max(240, Math.round(el.clientWidth * 0.6)), behavior: 'smooth' });
  };

  const scrollTabsPrev = () => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: -Math.max(240, Math.round(el.clientWidth * 0.6)), behavior: 'smooth' });
  };

  const popularScrollStepPx = () => {
    const el = popularScrollRef.current;
    if (!el) return POPULAR_CARD_STRIDE_FALLBACK_PX;
    const first = el.querySelector('.rdp-popular-card');
    if (!first) return POPULAR_CARD_STRIDE_FALLBACK_PX;
    const cs = getComputedStyle(el);
    const gap = parseFloat(cs.gap || cs.columnGap || '14') || 14;
    const w = first.getBoundingClientRect().width;
    return Math.max(200, Math.round(w + gap));
  };

  const animatePopularScrollBy = (delta) => {
    const el = popularScrollRef.current;
    if (!el || !delta) return;

    if (popularRafRef.current) {
      cancelAnimationFrame(popularRafRef.current);
      popularRafRef.current = 0;
    }

    const start = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;
    const target = Math.max(0, Math.min(start + delta, maxLeft));
    const change = target - start;
    if (Math.abs(change) < 0.5) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      el.scrollLeft = target;
      return;
    }

    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / POPULAR_SCROLL_ANIM_MS);
      el.scrollLeft = start + change * easeInOutQuad(t);
      if (t < 1) {
        popularRafRef.current = requestAnimationFrame(tick);
      } else {
        popularRafRef.current = 0;
        updatePopularScrollMeta();
      }
    };
    popularRafRef.current = requestAnimationFrame(tick);
  };

  const scrollPopular = (dir) => {
    const delta = popularScrollStepPx();
    animatePopularScrollBy((dir === 'prev' ? -1 : 1) * delta);
  };

  const scrollToSection = (targetId) => {
    if (targetId === POPULAR_TAB_ID) {
      popularRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    menuSectionRefs.current?.[targetId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onSelectTab = (targetId) => {
    setActiveMenuUuid(targetId);
    isAutoScrollingRef.current = true;
    if (suppressSpyTimeoutRef.current) window.clearTimeout(suppressSpyTimeoutRef.current);
    scrollToSection(targetId);
    suppressSpyTimeoutRef.current = window.setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, TAB_CLICK_SCROLL_SUPPRESS_MS);
  };

  const menuTabs = useMemo(() => {
    return [
      { id: POPULAR_TAB_ID, label: 'Popular Items' },
      ...categorySections.map((category) => ({ id: category.id, label: category.label })),
    ];
  }, [categorySections]);

  useEffect(() => {
    // Deterministic "stuck" detection (more reliable than IO when parents/overflow affect layout).
    const sentinel = stickyMenuSentinelRef.current;
    if (!sentinel) return undefined;

    const computeHeaderH = () => {
      const css = getComputedStyle(document.documentElement);
      return parseInt(css.getPropertyValue('--app-header-h') || '0', 10) || 0;
    };

    const update = () => {
      const headerH = computeHeaderH();
      const top = sentinel.getBoundingClientRect().top;
      // Stick when sentinel has crossed the bottom edge of the fixed header.
      setIsMenuStuck(top <= headerH + 1);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const sectionEls = [];
    const popularEl = popularRef.current;
    if (popularEl) sectionEls.push({ id: POPULAR_TAB_ID, el: popularEl });

    categorySections.forEach((category) => {
      const el = menuSectionRefs.current?.[category.id];
      if (el) sectionEls.push({ id: category.id, el });
    });

    if (!sectionEls.length) return undefined;

    const getTopOffsetPx = () => {
      const css = getComputedStyle(document.documentElement);
      const headerH = parseInt(css.getPropertyValue('--app-header-h') || '0', 10) || 0;
      const menuH = parseInt(css.getPropertyValue('--rdp-menu-h') || '0', 10) || 0;
      return headerH + menuH + 12;
    };

    const top = getTopOffsetPx();
    const io = new IntersectionObserver(
      (entries) => {
        if (isAutoScrollingRef.current) return;

        // Pick the most visible intersecting section.
        const candidates = entries
          .filter((e) => e.isIntersecting)
          .map((e) => ({ id: e.target.getAttribute('data-menu-id'), ratio: e.intersectionRatio, top: e.boundingClientRect.top }))
          .filter((x) => x.id);

        if (!candidates.length) return;
        candidates.sort((a, b) => (b.ratio - a.ratio) || (Math.abs(a.top) - Math.abs(b.top)));
        const nextId = candidates[0].id;
        setActiveMenuUuid((prev) => (prev === nextId ? prev : nextId));
      },
      {
        root: null,
        threshold: [0, 0.1, 0.25, 0.4, 0.6],
        // Offset the "top" by the fixed header + sticky menu so sections activate cleanly.
        rootMargin: `-${top}px 0px -55% 0px`,
      }
    );

    sectionEls.forEach(({ id, el }) => {
      el.setAttribute('data-menu-id', id);
      io.observe(el);
    });

    return () => io.disconnect();
  }, [categorySections]);

  return (
  <>
  <div className="rdp-page">
    <div className="rdp-wrapper">
      <div className="rdp-container">
        <div className="container">
          <div className="rdp-header" id="rdp-restaurant-top">
            <div className="rdp-header-top">
              <div className="rdp-hero">
                {(() => {
                  const candidates = [
                    restaurant?.banner_url,
                    restaurant?.pick_of_the_day_banner_url,
                  ].filter((u) => typeof u === 'string' && u.trim());
                  const currentSrc = candidates[bannerCandidateIdx];
                  if (currentSrc) {
                    return (
                      <img
                        src={currentSrc}
                        alt={restaurant?.name || 'Restaurant'}
                        className="rdp-hero-img"
                        onError={() => setBannerCandidateIdx((i) => i + 1)}
                      />
                    );
                  }
                  return (
                    <div
                      className="rdp-hero-img rdp-hero-img--placeholder"
                      role="img"
                      aria-label={restaurant?.name || 'Restaurant banner'}
                    >
                      <span className="rdp-hero-placeholder-text">
                        {(restaurant?.name || 'RESTAURANT').toUpperCase()}
                      </span>
                    </div>
                  );
                })()}
              </div>
              <div className="rdp-info">
                {loading ? (
                  <Heading level={1} className="rdp-title">Loading?</Heading>
                ) : (
                  <>
                    <Heading level={1} className="rdp-title">{restaurant?.name || '?'}</Heading>
                    {restaurant?.description ? (
                      <div className="rdp-subtitle">{restaurant.description}</div>
                    ) : null}
                  </>
                )}

                <div className="rdp-details">
                  {error ? (
                    <div className="text-danger small" role="alert">{error}</div>
                  ) : null}

                  {orderBlock ? (
                    <p className="rdp-delivery-notice" role="status">
                      {orderBlock.message}
                    </p>
                  ) : null}

                  <div className="rdp-detail-row rdp-detail-row--address">
                    <img className="rdp-detail-icon" src={locationIcon} alt="" />
                    <div className="rdp-detail-text">{restaurant?.address || '\u2014'}
                      {branchDistanceLabel ? (
                        <span className="rdp-detail-distance">{branchDistanceLabel}</span>
                      ) : null}</div>
                  </div>

                  <div className="rdp-details-grid">
                    <div className="rdp-detail-row">
                      <img className="rdp-detail-icon" src={phoneIcon} alt="" />
                      <div className="rdp-detail-text">
                        {phoneDisplay !== '\u2014' ? (
                          <a className="rdp-detail-link" href={`tel:${phoneDisplay.replace(/\s/g, '')}`}>
                            {phoneDisplay}
                          </a>
                        ) : (
                          phoneDisplay
                        )}
                      </div>
                    </div>

                    <div className="rdp-detail-row">
                      <IconGlobe />
                      <div className="rdp-detail-text">
                        {websiteUrl ? (
                          <a
                            className="rdp-detail-link"
                            href={websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {websiteUrl}
                          </a>
                        ) : (
                          <span className="rdp-detail-text--muted">{'\u2014'}</span>
                        )}
                      </div>
                    </div>

                    <div className="rdp-detail-row">
                      <IconClock />
                      <div className="rdp-detail-text">{hoursLabel}</div>
                    </div>

                    <div className="rdp-detail-row">
                      <IconStorefront />
                      <div
                        className={`rdp-detail-text rdp-detail-text--status rdp-detail-text--status-${statusClass}`}
                        role="status"
                      >
                        {statusLabel}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rdp-toolbar">
              {/* Search moved into sticky menu for better UX while scrolling */}
            </div>
          </div>

          <div ref={stickyMenuSentinelRef} className="rdp-stickyMenu-sentinel" aria-hidden="true" />

          <div className="rdp-cards" ref={cardsRef}>
            <StickyMenu
              stickToRef={cardsRef}
              search={(
                <div className="rdp-search">
                  <img src={searchIcon} alt="Search" className="rdp-search-icon" />
                  <input
                    className="rdp-search-input"
                    type="text"
                    placeholder="Search menu items"
                    aria-label="Search menu items"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
              )}
              tabsRef={tabsRef}
              isStuck={isMenuStuck}
              tabs={menuTabs}
              activeTabId={activeMenuUuid}
              onSelectTab={onSelectTab}
              onScrollTabsNext={scrollTabsNext}
              onScrollTabsPrev={scrollTabsPrev}
            />
            {loading ? (
              <div className="text-muted small">Loading items?</div>
            ) : (filteredItemsAll.length || popularItems.length) ? (
              <div className="rdp-content">
                <section className="rdp-section" ref={popularRef} data-menu-id={POPULAR_TAB_ID}>
                  <div className="rdp-section-head">
                    <SectionHeading className="rdp-section-heading">POPULAR ITEMS</SectionHeading>
                  </div>

                  {popularItems.length ? (
                    <div className="rdp-popular-row">
                      {popularScrollMeta.hasOverflow && (
                        <button
                          className="rdp-circle-nav rdp-circle-nav--prev"
                          type="button"
                          onClick={() => scrollPopular('prev')}
                          disabled={!popularScrollMeta.canScrollLeft}
                          aria-label="Scroll popular items left"
                        >
                          <img src={cuisineArrowIcon} alt="" className="rdp-circle-nav-icon rdp-circle-nav-icon--prev" />
                        </button>
                      )}

                      <div className="rdp-popular-scroll-wrap">
                        <div className="rdp-popular-scroll" ref={popularScrollRef} onScroll={updatePopularScrollMeta}>
                        {popularItems.map((it) => {
                          const itHasPhoto = hasUsableImage(it);
                          const itColor = resolveItemColor(it);
                          return (
                          <div key={it.uuid || it.id} className="rdp-popular-card">
                            <div className="rdp-popular-img-wrap">
                              {itHasPhoto ? (
                                <img
                                  className="rdp-popular-img"
                                  src={it.item_photo_url}
                                  alt={itemLabel(it) || '?'}
                                  onError={() => markImageBroken(it)}
                                  onLoad={updatePopularScrollMeta}
                                />
                              ) : (
                                <div
                                  className="rdp-popular-img rdp-popular-img--color"
                                  style={
                                    itColor
                                      ? { backgroundColor: itColor, color: readableTextOn(itColor) }
                                      : undefined
                                  }
                                  role="img"
                                  aria-label={itemLabel(it) || '?'}
                                >
                                  <span className="rdp-thumb-initial" aria-hidden="true">{itemInitial(it)}</span>
                                </div>
                              )}
                              <button
                                type="button"
                                className={`rdp-add-btn rdp-add-btn--popular${!canOrder ? ' rdp-add-btn--blocked' : ''}`}
                                aria-label={`Add ${itemLabel(it) || '?'}`}
                                aria-disabled={!canOrder}
                                onClick={() => handleAddItemClick(it)}
                              >
                                <img className="rdp-add-icon rdp-add-icon--popular" src={addToCartIcon} alt="" aria-hidden="true" />
                              </button>
                            </div>
                            <div className="rdp-popular-body">
                              <div className="rdp-popular-title">{itemLabel(it) || '?'}</div>
                              <div className="rdp-popular-meta">
                                {it.price ? <div className="rdp-popular-price">${it.price}</div> : <div />}
                              </div>
                            </div>
                          </div>
                          );
                        })}
                        </div>
                      </div>

                      {popularScrollMeta.hasOverflow && (
                        <button
                          className="rdp-circle-nav rdp-circle-nav--next"
                          type="button"
                          onClick={() => scrollPopular('next')}
                          disabled={!popularScrollMeta.canScrollRight}
                          aria-label="Scroll popular items right"
                        >
                          <img src={cuisineArrowIcon} alt="" className="rdp-circle-nav-icon" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted small">No popular items found.</div>
                  )}
                </section>

                {categorySections.map((category) => {
                  const sectionItems = category.items || [];
                  if (!sectionItems.length) return null;

                  return (
                    <section
                      key={category.id}
                      className="rdp-section"
                      ref={(el) => { if (el) menuSectionRefs.current[category.id] = el; }}
                      data-menu-id={category.id}
                    >
                      <div className="rdp-section-head">
                        <SectionHeading className="rdp-section-heading">{category.label.toUpperCase()}</SectionHeading>
                      </div>

                      <div className="rdp-menu-grid">
                        {sectionItems.map((it) => {
                          const itHasPhoto = hasUsableImage(it);
                          const itColor = resolveItemColor(it);
                          return (
                          <div key={it.uuid || it.id} className="rdp-menu-item">
                            {itHasPhoto ? (
                              <img
                                className="rdp-menu-thumb"
                                src={it.item_photo_url}
                                alt={itemLabel(it) || '?'}
                                onError={() => markImageBroken(it)}
                              />
                            ) : (
                              <div
                                className="rdp-menu-thumb rdp-menu-thumb--color"
                                style={
                                  itColor
                                    ? { backgroundColor: itColor, color: readableTextOn(itColor) }
                                    : undefined
                                }
                                role="img"
                                aria-label={itemLabel(it) || '?'}
                              >
                                <span className="rdp-thumb-initial" aria-hidden="true">{itemInitial(it)}</span>
                              </div>
                            )}

                            <div className="rdp-menu-body">
                              <div className="rdp-menu-title">{itemLabel(it) || '?'}</div>
                              {itemDescription(it) ? (
                                <div className="rdp-menu-desc">{itemDescription(it)}</div>
                              ) : null}
                              {it.price ? <div className="rdp-menu-price">${it.price}</div> : null}
                            </div>

                            <button
                              type="button"
                              className={`rdp-add-btn${!canOrder ? ' rdp-add-btn--blocked' : ''}`}
                              aria-label={`Add ${itemLabel(it) || '?'}`}
                              aria-disabled={!canOrder}
                              onClick={() => handleAddItemClick(it)}
                            >
                              <img className="rdp-add-icon" src={addToCartIcon} alt="" aria-hidden="true" />
                            </button>
                          </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted small">No items found.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>

  {customizeItem ? (
    <MenuItemCustomizeModal
      key={String(customizeItem.uuid || customizeItem.id)}
      item={customizeItem}
      restaurant={restaurant}
      comboExtraItems={comboExtraItemsForModal}
      itemLabel={itemLabel}
      itemDescription={itemDescription}
      orderingDisabled={!canOrder}
      orderBlock={orderBlock}
      onOrderingBlocked={() => orderBlock && setOrderBlockModal(orderBlock)}
      onClose={() => setCustomizeItem(null)}
    />
  ) : null}

  <OrderingBlockedModal
    open={Boolean(orderBlockModal)}
    block={orderBlockModal}
    onClose={() => setOrderBlockModal(null)}
  />
  </>
  );
}
